import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { useIntegrityCheck, IntegrityReportModal } from './admin/IntegrityCheck';
import { CountryBackfillPanel } from './admin/CountryBackfillPanel';
import { AdvisorDiscountBackfillPanel } from './admin/AdvisorDiscountBackfillPanel';
import { PortfolioDistributionCard } from './admin/PortfolioDistributionCard';
import { EmailSegmentPanel } from './admin/EmailSegmentPanel';
import logger from '../utils/logger';
import {
  UserRowSkeleton, UsersTable, AdvancedFiltersPanel, UsersResultsPane,
} from './AdminUsersTabParts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ---------- Subcomponents ----------

// ----- Quick Filters chips -----

const PRIVILEGED_ROLES = ['owner', 'admin', 'infosec'];

const arrayEq = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function isAllDefault(s) {
  return !s.search && !s.role && s.roleList.length === 0 && !s.authType
    && !s.country.trim() && s.status === 'active' && s.isBetaTester === null && !s.createdAfter
    && !s.accountAgeBucket && !s.lastLoginWithinDays && !s.activityStatus;
}

function getActiveChip(s) {
  if (isAllDefault(s)) return 'all';
  // Each chip matches when its specific axis is set AND all other axes are at default.
  // We allow non-matching axes only if they're at default to keep the active chip unambiguous.
  const baseClean = !s.search && !s.country.trim()
    && !s.accountAgeBucket && !s.lastLoginWithinDays && !s.activityStatus;
  if (s.isBetaTester === true && !s.role && s.roleList.length === 0 && !s.authType
      && s.status === 'active' && !s.createdAfter && baseClean) return 'beta_testers';
  if (s.createdAfter && !s.role && s.roleList.length === 0 && !s.authType
      && s.isBetaTester === null && s.status === 'active' && baseClean) return 'new_this_week';
  if (s.authType === 'google' && !s.role && s.roleList.length === 0
      && s.status === 'active' && s.isBetaTester === null && !s.createdAfter && baseClean) return 'google_auth';
  if (s.status === 'deleted' && !s.role && s.roleList.length === 0 && !s.authType
      && s.isBetaTester === null && !s.createdAfter && baseClean) return 'deleted';
  if (arrayEq(s.roleList, PRIVILEGED_ROLES) && !s.role && !s.authType
      && s.status === 'active' && s.isBetaTester === null && !s.createdAfter && baseClean) return 'privileged';
  return null; // custom combination from dropdowns
}

function QuickFilters({ state, onApply, onReset }) {
  const active = getActiveChip(state);
  const chips = [
    { key: 'all', label: 'All', testid: 'qf-all', preset: null },
    { key: 'beta_testers', label: 'Beta testers', testid: 'qf-beta', preset: { isBetaTester: true } },
    { key: 'new_this_week', label: 'New this week', testid: 'qf-new', preset: { createdAfter: isoDaysAgo(7) } },
    { key: 'google_auth', label: 'Google sign-in', testid: 'qf-google', preset: { authType: 'google' } },
    { key: 'deleted', label: 'Deleted', testid: 'qf-deleted', preset: { status: 'deleted' } },
    { key: 'privileged', label: 'Owners & Admins', testid: 'qf-privileged', preset: { roleList: PRIVILEGED_ROLES } },
  ];

  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="admin-users-quick-filters">
      {chips.map(chip => {
        const isActive = active === chip.key;
        return (
          <button
            key={chip.key}
            onClick={() => (chip.preset === null ? onReset() : onApply(chip.preset))}
            data-testid={chip.testid}
            data-active={isActive}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
              isActive
                ? 'bg-green-500 text-white border-green-500'
                : 'bg-transparent text-gray-300 border-gray-600 hover:border-gray-400 hover:text-white'
            }`}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

function UsersFilters({ search, setSearch, role, setRole, authType, setAuthType, status, setStatus, country, setCountry, onReset, hasActiveFilter }) {
  return (
    <div className="space-y-3 mb-4" data-testid="admin-users-filters">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email or name…"
          className="w-full pl-10 pr-10 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent"
          data-testid="admin-users-search-input"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            data-testid="admin-users-search-clear"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
          data-testid="admin-users-filter-role"
        >
          <option value="">All Roles</option>
          <option value="user">User</option>
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="infosec">InfoSec</option>
          <option value="engineer">Engineer</option>
          <option value="product_manager">Product Manager</option>
          <option value="support">Support</option>
        </select>

        <select
          value={authType}
          onChange={(e) => setAuthType(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
          data-testid="admin-users-filter-auth"
        >
          <option value="">All Auth Types</option>
          <option value="email">Email</option>
          <option value="google">Google</option>
        </select>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
          data-testid="admin-users-filter-status"
        >
          <option value="active">Active</option>
          <option value="deleted">Deleted</option>
          <option value="all">All</option>
        </select>

        <input
          type="text"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Country (exact)"
          className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-hidden focus:ring-2 focus:ring-green-500"
          data-testid="admin-users-filter-country"
        />
      </div>

      {hasActiveFilter && (
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white"
          data-testid="admin-users-filter-reset"
        >
          <Filter className="w-3 h-3" />
          Reset filters
        </button>
      )}
    </div>
  );
}

// RateControl, UsersTable, AdvancedFiltersPanel, UsersResultsPane moved to AdminUsersTabParts.js

const DEFAULT_FILTER_STATE = {
  search: '', role: '', roleList: [], authType: '', status: 'active', country: '',
  isBetaTester: null, createdAfter: '', accountAgeBucket: '',
  lastLoginWithinDays: '', activityStatus: '', portfolioVolume: '',
};

function buildCampaignFilters({ status, roleList, role, authType, country, isBetaTester, createdAfter, accountAgeBucket, lastLoginWithinDays, activityStatus }) {
  // Snapshot of the current filters in the exact shape /api/admin/campaigns/preview expects.
  // Note: free-text 'search' is intentionally NOT forwarded — the backend can't index
  // search through encrypted PII for audience resolution.
  return {
    status,
    ...(roleList.length > 0 ? { roles: roleList.join(',') } : (role ? { role } : {})),
    ...(authType ? { auth_type: authType } : {}),
    ...(country.trim() ? { country: country.trim() } : {}),
    ...(isBetaTester === true ? { is_beta_tester: true } : isBetaTester === false ? { is_beta_tester: false } : {}),
    ...(createdAfter ? { created_after: createdAfter } : {}),
    ...(accountAgeBucket ? { account_age_bucket: accountAgeBucket } : {}),
    ...(lastLoginWithinDays ? { last_login_within_days: Number(lastLoginWithinDays) } : {}),
    ...(activityStatus ? { activity_status: activityStatus } : {}),
  };
}

export const AdminUsersTab = ({ refreshKey, onDeleteUser, fetchUsers, isActive = true }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchCapHit, setSearchCapHit] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [role, setRole] = useState('');
  const [roleList, setRoleList] = useState([]);
  const [authType, setAuthType] = useState('');
  const [status, setStatus] = useState('active');
  const [country, setCountry] = useState('');
  const [isBetaTester, setIsBetaTester] = useState(null); // null | true | false
  const [createdAfter, setCreatedAfter] = useState(''); // ISO string
  const [accountAgeBucket, setAccountAgeBucket] = useState(''); // '' | 'under_90' | '91_180' | '181_365' | 'over_365'
  const [lastLoginWithinDays, setLastLoginWithinDays] = useState(''); // '' | '7' | '30' | '90'
  const [activityStatus, setActivityStatus] = useState(''); // '' | 'active' | 'inactive' | 'dormant'
  const [portfolioVolume, setPortfolioVolume] = useState(''); // '' | no_portfolio | under_100k | 100k_500k | 500k_1m | 1m_5m | over_5m
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const { checking, report, applying, runCheck, applyFixes, closeReport } = useIntegrityCheck();
  const hasFetchedRef = useRef(false);

  // Debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever filters/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, role, roleList, authType, status, country, isBetaTester, createdAfter, accountAgeBucket, lastLoginWithinDays, activityStatus, portfolioVolume]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: String((page - 1) * PAGE_SIZE),
        limit: String(PAGE_SIZE),
        status,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (roleList.length > 0) params.set('roles', roleList.join(','));
      else if (role) params.set('role', role);
      if (authType) params.set('auth_type', authType);
      if (country.trim()) params.set('country', country.trim());
      if (isBetaTester === true) params.set('is_beta_tester', 'true');
      else if (isBetaTester === false) params.set('is_beta_tester', 'false');
      if (createdAfter) params.set('created_after', createdAfter);
      if (accountAgeBucket) params.set('account_age_bucket', accountAgeBucket);
      if (lastLoginWithinDays) params.set('last_login_within_days', lastLoginWithinDays);
      if (activityStatus) params.set('activity_status', activityStatus);
      if (portfolioVolume) params.set('portfolio_volume', portfolioVolume);

      const response = await fetch(`${API_URL}/api/admin/users?${params.toString()}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      // Backward-compatible: support both wrapped and legacy list responses
      if (Array.isArray(data)) {
        setUsers(data);
        setTotal(data.length);
        setSearchCapHit(false);
      } else {
        setUsers(data.users || []);
        setTotal(data.total || 0);
        setSearchCapHit(Boolean(data.search_cap_hit));
      }
    } catch (err) {
      logger.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
      hasFetchedRef.current = true;
    }
  }, [page, debouncedSearch, role, roleList, authType, status, country, isBetaTester, createdAfter, accountAgeBucket, lastLoginWithinDays, activityStatus, portfolioVolume]);

  // Load when params change OR when external refresh is triggered (and tab is active)
  useEffect(() => {
    if (!isActive) return;
    load();
  }, [load, isActive, refreshKey]);

  const handleReset = () => {
    setSearch(DEFAULT_FILTER_STATE.search); setRole(DEFAULT_FILTER_STATE.role);
    setRoleList(DEFAULT_FILTER_STATE.roleList); setAuthType(DEFAULT_FILTER_STATE.authType);
    setStatus(DEFAULT_FILTER_STATE.status); setCountry(DEFAULT_FILTER_STATE.country);
    setIsBetaTester(DEFAULT_FILTER_STATE.isBetaTester); setCreatedAfter(DEFAULT_FILTER_STATE.createdAfter);
    setAccountAgeBucket(DEFAULT_FILTER_STATE.accountAgeBucket);
    setLastLoginWithinDays(DEFAULT_FILTER_STATE.lastLoginWithinDays);
    setActivityStatus(DEFAULT_FILTER_STATE.activityStatus);
    setPortfolioVolume(DEFAULT_FILTER_STATE.portfolioVolume);
  };

  const advancedActiveCount = (accountAgeBucket ? 1 : 0) + (lastLoginWithinDays ? 1 : 0) + (activityStatus ? 1 : 0) + (portfolioVolume ? 1 : 0);
  const hasAnyFilter = debouncedSearch || role || roleList.length > 0 || authType || country.trim()
    || status !== 'active' || isBetaTester !== null || createdAfter || advancedActiveCount > 0;
  const showEmptyState = !loading && users.length === 0 && !error;

  const applyPreset = (preset) => {
    handleReset();
    if (preset.role !== undefined) setRole(preset.role);
    if (preset.roleList !== undefined) setRoleList(preset.roleList);
    if (preset.authType !== undefined) setAuthType(preset.authType);
    if (preset.status !== undefined) setStatus(preset.status);
    if (preset.isBetaTester !== undefined) setIsBetaTester(preset.isBetaTester);
    if (preset.createdAfter !== undefined) setCreatedAfter(preset.createdAfter);
  };

  const filterState = { search: debouncedSearch, role, roleList, authType, status, country, isBetaTester, createdAfter, accountAgeBucket, lastLoginWithinDays, activityStatus, portfolioVolume };
  const campaignFilters = buildCampaignFilters(filterState);

  // When user picks a single role from the dropdown, drop any multi-role chip selection
  const setRoleClearList = (next) => {
    setRoleList([]);
    setRole(next);
  };

  return (
    <div className="bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-700" data-testid="admin-users-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-bold text-white">Users Management</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <EmailSegmentPanel filters={campaignFilters} />
          <p className="text-sm text-gray-400" data-testid="admin-users-total">
            {loading && !hasFetchedRef.current ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'user' : 'users'}${hasAnyFilter ? ' (filtered)' : ''}`}
          </p>
        </div>
      </div>

      <CountryBackfillPanel onApplied={load} />
      <AdvisorDiscountBackfillPanel onApplied={load} />
      <PortfolioDistributionCard onRecalculated={load} />

      <QuickFilters state={filterState} onApply={applyPreset} onReset={handleReset} />

      <UsersFilters
        search={search} setSearch={setSearch}
        role={role} setRole={setRoleClearList}
        authType={authType} setAuthType={setAuthType}
        status={status} setStatus={setStatus}
        country={country} setCountry={setCountry}
        onReset={handleReset}
        hasActiveFilter={!!(role || authType || country || status !== 'active' || advancedActiveCount > 0)}
      />

      {/* Advanced filters — collapsible to keep the default view clean */}
      <AdvancedFiltersPanel
        advancedOpen={advancedOpen} setAdvancedOpen={setAdvancedOpen}
        advancedActiveCount={advancedActiveCount}
        accountAgeBucket={accountAgeBucket} setAccountAgeBucket={setAccountAgeBucket}
        lastLoginWithinDays={lastLoginWithinDays} setLastLoginWithinDays={setLastLoginWithinDays}
        activityStatus={activityStatus} setActivityStatus={setActivityStatus}
        portfolioVolume={portfolioVolume} setPortfolioVolume={setPortfolioVolume}
      />

      {searchCapHit && (
        <div className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs" data-testid="admin-users-cap-warning">
          Search scanned the most recent 10,000 users; older matches may be hidden. Add a role, country, or status filter to narrow the cohort.
        </div>
      )}

      <UsersResultsPane
        loading={loading} users={users} error={error}
        hasAnyFilter={hasAnyFilter} page={page} total={total} setPage={setPage}
        onDeleteUser={onDeleteUser} checking={checking} runCheck={runCheck}
        setUsers={setUsers} handleReset={handleReset} load={load}
        showEmptyState={showEmptyState}
      />

      <div className="mt-6 p-4 bg-gray-900/50 rounded-lg border border-gray-700">
        <h4 className="text-sm font-semibold text-white mb-2">About Soft Delete</h4>
        <p className="text-xs text-gray-400">
          Deleting a user will mark them and their data as &quot;deleted&quot; but preserve it for audit purposes.
          The user will be able to create a fresh access request and start over.
        </p>
      </div>

      {report && (
        <IntegrityReportModal
          report={report}
          onClose={closeReport}
          onApplyFixes={() => applyFixes(fetchUsers)}
          applying={applying}
        />
      )}
    </div>
  );
};
